import { Link } from 'react-router-dom';
import type { Estate } from '../types';

interface EstateCardProps {
  estate: Estate;
}

// Default: pull un-hashed images from the legacy chateaushi.com/images path,
// which web-2 now serves both via its API server and the static SPA deploy.
// For local preview without internet, set VITE_IMAGE_BASE_URL=http://localhost:5001/images
// (pointing at the local web-2 server) or /images_project (the public symlink).
const IMAGE_BASE_URL =
  import.meta.env.VITE_IMAGE_BASE_URL || 'https://chateaushi.com/images';

const resolveImageUrl = (path: string) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  // JSON stores paths like "previews/foo.jpg" or "/previews/foo.jpg"; normalise.
  return `${IMAGE_BASE_URL}/${path.replace(/^\/+/, '')}`;
};

export function EstateCard({ estate }: EstateCardProps) {
  const imageUrl = resolveImageUrl(estate.preview);

  return (
    <Link
      to={`/estate/${estate.id}`}
      className="group block animate-slide-up"
    >
      <article className="flex flex-col overflow-hidden">
        {/* Image */}
        <div className="relative h-72 overflow-hidden bg-slate-800 rounded-sm">
          <img
            src={imageUrl}
            alt={estate.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
          <div className="absolute bottom-4 left-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-200">
              {estate.location}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col gap-2 pt-3">
          <div>
            <h3 className="font-serif text-xl md:text-2xl text-foreground group-hover:text-white transition-colors line-clamp-1">
              {estate.name}
            </h3>
            <p className="mt-2 text-sm text-slate-300 line-clamp-2">
              {estate.summary}
            </p>
          </div>
          <div className="mt-auto flex justify-between items-center pt-2">
            <span className="font-serif text-sm tracking-wide text-foreground border border-white/80 px-5 py-1.5 rounded-sm backdrop-blur-sm bg-white/5 group-hover:bg-foreground group-hover:text-primary transition-all duration-300">
              Explore
            </span>
            <span className="text-sm text-slate-400">
              {estate.bedrooms} bedrooms
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
}
